/**
 * TASKS-ACTION-PLANS.WIDGET
 * List of actions plans
 */

import {
  faCopy,
  faEllipsisV,
  faPaperPlane,
  faPencilAlt,
  faTrash,
  faUserCircle
} from "@fortawesome/free-solid-svg-icons"
import Card from "@/components/card"
import ListButton from "@/components/list-button"
import ListCell from "@/components/list-cell"
import ListFields from "@/components/list-fields"
import ListIcon from "@/components/list-icon"
import ListItem from "@/components/list-item"
import Space from "@/components/space"
import { store } from "@/index"
import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Session } from "@/redux/_session.types"
import {
  actionPlanActivate,
  actionPlanFetch,
  actionPlanFetchSelf,
  actionPlanGet,
  actionPlanSend
} from "@/redux/action-plan.actions"
import { ActionPlan, ActionPlanState } from "@/redux/action-plan.types"
import { userActivate } from "@/redux/user.actions"
import AdmUserPopulationsWidget from "./adm-user-populations.widget"
import Chip from "@/components/chip"
import ProgressBar from "@/components/progress-bar"
import { useEffect, useState } from "react"
import { STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { topicGet } from "@/redux/topic.actions"
import { toast } from "react-toastify"
import TasksDeleteModal from "@/modals/tasks-delete.modal"
import { Survey } from "@/redux/survey.types"
import PageLoader from "@/components/page-loader"
import PagePlaceholder from "@/components/page-placeholder"
import GetSurveyResultsModal from "@/modals/get-survey-results.modal"
import { surveyActivate } from "@/redux/survey.actions"

interface StateProps extends WithTranslation {
  _session: Session
  actionPlan: ActionPlanState
}

interface OwnProps {
  isSelf?: boolean
}

type Props = StateProps & OwnProps

const ACTION_DELETE = "ACTION_DELETE"
const ACTION_EDIT = "ACTION_EDIT"
const ACTION_COPY_LINK = "ACTION_COPY_LINK"
const ACTION_SEND_EMAIL = "ACTION_SEND_EMAIL"
const MODAL_GET_SURVEY = "MODAL_GET_SURVEY"

function TasksActionPlansWidget(props: Props) {
  const { t } = props

  const [modal, setModal] = useState<string | null>(null)
  const [surveys, setSurveys] = useState<Survey[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadData()

    async function loadData() {
      setIsLoading(true)

      let response: any = []
      if (props.isSelf) {
        response = await store.dispatch(actionPlanFetchSelf())
      } else {
        response = await store.dispatch(actionPlanFetch())
      }

      store.dispatch(topicGet([]))

      if (!response.error) {
        if (!props.isSelf) {
          response = response.filter((x) => x.User?.id !== props._session.userId && x.User)
        }

        store.dispatch(actionPlanGet(response))

        const surveysUniq: Survey[] = Array.from(
          new Map<number, Survey>(response.map((x: any) => [x.Survey.id, new Survey(x.Survey)])).values()
        )
        setSurveys(surveysUniq)
      } else {
        store.dispatch(actionPlanGet([]))
        setSurveys([])
      }

      setIsLoading(false)
    }
  }, [])

  //Select an action from context menu
  function clickAction(action: string, actionPlan: ActionPlan) {
    store.dispatch(actionPlanActivate(actionPlan))
    store.dispatch(userActivate(actionPlan.User))

    switch (action) {
      case ACTION_COPY_LINK:
        navigator.clipboard.writeText(actionPlan.User.getInvitationLink(props._session, "tasks"))
        return toast(t("utils_copy_link_done"))
      case ACTION_DELETE:
        return setModal(ACTION_DELETE)
      case ACTION_EDIT:
        return editActionPlan(actionPlan)
      case ACTION_SEND_EMAIL:
        return send(actionPlan)
      default:
        return null
    }
  }

  //Edit draft action plan
  function editActionPlan(actionPlan: ActionPlan) {
    store.dispatch(actionPlanActivate(actionPlan))
    store.dispatch(surveyActivate(actionPlan.Survey))
    store.dispatch(userActivate(actionPlan.User))
    setModal(MODAL_GET_SURVEY)
  }

  async function send(actionPlan: ActionPlan) {
    const response: any = await store.dispatch(
      actionPlanSend(actionPlan.id, props._session.userName, actionPlan.User.getInvitationLink(props._session, "tasks"))
    )
    if (!response.error) toast(t("invitation_send_email_confirm", { email: actionPlan.User.email }))
  }

  return (
    <>
      {modal === MODAL_GET_SURVEY && (
        <GetSurveyResultsModal
          actionPlanId={props.actionPlan.active.id}
          onClose={() => setModal(null)}
          destination="/tasks/edit"
        />
      )}

      {modal === ACTION_DELETE && (
        <TasksDeleteModal
          onClose={() => setModal(null)}
          onDelete={() =>
            store.dispatch(actionPlanGet(props.actionPlan.list.filter((x) => x.id !== props.actionPlan.active.id)))
          }
        />
      )}

      {props.isSelf ? (
        <PageLoader status={isLoading ? STATUS_LOADING : STATUS_LOADED}>
          <div className="flex flex-wrap">
            {props.actionPlan.list.length === 0 && <PagePlaceholder title={t("tasks_empty")} image="edit" />}
            {props.actionPlan.list.map((actionPlan) => (
              <Card key={actionPlan.id} onClick={() => editActionPlan(actionPlan)} className="_hover mr-20 w-[200px]">
                <div className="text-lg">
                  {actionPlan.priority.length > 0 ? actionPlan.priority : actionPlan.Survey.name}
                </div>
                <div className="text-sm grey-t h-20">
                  {actionPlan.priority.length > 0 ? actionPlan.Survey.name : null}
                </div>
                {actionPlan.status === "active" ? (
                  <div className="flex">
                    <Chip>{actionPlan.statusLabel}</Chip>
                    <Space />
                    <div style={{ margin: "-6px 0px", width: 120 }}>
                      <ProgressBar items={[{ value: actionPlan.tasksDoneCount }]} max={actionPlan.tasks.length} />
                    </div>
                  </div>
                ) : (
                  <Chip>{actionPlan.statusLabel}</Chip>
                )}
              </Card>
            ))}
          </div>
        </PageLoader>
      ) : (
        <>
          {props.actionPlan.list.length === 0 && <h3>{t("action_plan_no_active")}</h3>}

          {surveys.map((survey) => (
            <div key={survey.id}>
              <h3>{survey.name}</h3>

              <Card isWithoutPadding status={isLoading ? STATUS_LOADING : STATUS_LOADED}>
                <ListFields>
                  <ListCell />
                  <ListCell
                    width={170}
                    text={props._session.userRole === "ADMIN" ? t("tasks_teams_manager") : t("tasks_contact")}
                  />
                  <ListCell width={155} text={t("tasks_teams_populations")} />
                  <Space />
                  <ListCell width={400} text={t("tasks_progress")} />
                </ListFields>

                {props.actionPlan.list
                  .filter((x) => x.SurveyId === survey.id)
                  .map((actionPlan: ActionPlan) => (
                    <ListItem key={actionPlan.id} onClick={() => editActionPlan(actionPlan)} isEditable>
                      <ListIcon fallbackIcon={faUserCircle} image={actionPlan.User.imageUrl} />
                      <ListCell width={170} text={actionPlan.User.username} />
                      <AdmUserPopulationsWidget currentUser={actionPlan.User} />
                      <ListCell width={400}>
                        <div className="flex">
                          {actionPlan.status === "active" ? (
                            <div style={{ width: 120 }}>
                              <div style={{ margin: "-6px 0px" }}>
                                <ProgressBar
                                  items={[{ value: actionPlan.tasksDoneCount }]}
                                  max={actionPlan.tasks.length}
                                />
                              </div>
                            </div>
                          ) : (
                            <Chip>{actionPlan.statusLabel}</Chip>
                          )}
                        </div>

                        <Space />

                        <ListButton
                          onAction={(action: string) => clickAction(action, actionPlan)}
                          icon={faEllipsisV}
                          actions={[
                            { id: ACTION_EDIT, icon: faPencilAlt, label: t("utils_edit") },
                            { id: ACTION_DELETE, icon: faTrash, label: t("tasks_delete") },
                            {
                              id: ACTION_COPY_LINK,
                              icon: faCopy,
                              label: t("utils_copy_link")
                            },
                            { id: ACTION_SEND_EMAIL, icon: faPaperPlane, label: t("invitation_send_email") }
                          ]}
                        />
                      </ListCell>
                    </ListItem>
                  ))}
              </Card>
            </div>
          ))}
        </>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  actionPlan: state.actionPlan
})

export default connect(mapStateToProps)(withTranslation()(TasksActionPlansWidget))
